import type { MountVoiceAgentOptions, UiLocale } from "@microsoft/voice-widget-ui";

type Ui = NonNullable<MountVoiceAgentOptions["ui"]>;
type Theme = NonNullable<Ui["theme"]>;

export function parseThemeAttribute(value: string | null): Theme | undefined {
  return value === "auto" || value === "light" || value === "dark" ? value : undefined;
}

export function parseAccentColorAttribute(value: string | null): string | undefined {
  return value?.trim() ? value : undefined;
}

/**
 * Parse the `orb-palette` attribute — a JSON object with `base` + `highlight`
 * CSS colors. Blank throws (like lang-strings/ui-locale: "remove to restore
 * defaults"); malformed JSON throws (author error). A well-formed object with a
 * wrong shape is returned as-is and normalized/fallen-back downstream in the UI
 * layer, so a bad palette never breaks mount.
 */
export function parseOrbPaletteAttribute(value: string | null): Ui["orbPalette"] {
  if (value === null) return undefined;
  if (!value.trim()) {
    throw new Error(
      "<voice-agent>: orb-palette cannot be blank; remove the attribute to restore defaults.",
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    throw new Error(
      `<voice-agent>: invalid orb-palette JSON — ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("<voice-agent>: orb-palette must be a JSON object.");
  }
  return parsed as Ui["orbPalette"];
}

export function parseLangStringsAttribute(value: string | null): Ui["langStrings"] {
  if (value === null) return undefined;
  if (!value.trim()) {
    throw new Error(
      "<voice-agent>: lang-strings cannot be blank; remove the attribute to restore defaults.",
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    throw new Error(
      `<voice-agent>: invalid lang-strings JSON — ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("<voice-agent>: lang-strings must be a JSON object.");
  }
  return parsed as Ui["langStrings"];
}

export function parseUiLocaleAttribute(value: string | null): UiLocale {
  // Attribute removed -> restore defaults, matching theme/lang-strings removal.
  // The keys must be present (not `{}`) so setUiLocale clears lang/dir and resets
  // strings to English rather than treating the update as a no-op.
  if (value === null) return { lang: undefined, dir: undefined, strings: undefined };
  if (!value.trim()) {
    throw new Error(
      "<voice-agent>: ui-locale cannot be blank; remove the attribute to restore defaults.",
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    throw new Error(
      `<voice-agent>: invalid ui-locale JSON — ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("<voice-agent>: ui-locale must be a JSON object.");
  }
  const obj = parsed as Record<string, unknown>;
  const locale: UiLocale = {};
  if (typeof obj.lang === "string") locale.lang = obj.lang;
  if (obj.dir === "ltr" || obj.dir === "rtl" || obj.dir === "auto") locale.dir = obj.dir;
  if (typeof obj.strings === "object" && obj.strings !== null && !Array.isArray(obj.strings)) {
    locale.strings = obj.strings as UiLocale["strings"];
  }
  return locale;
}

/**
 * Presence-based boolean attribute (HTML idiom): absent -> `undefined`; a bare
 * attribute (`mute-button`) or any value outside the falsy set -> `true`;
 * `false`/`0`/`no`/`off` (case-insensitive) -> `false`.
 */
function parseBool(value: string | null): boolean | undefined {
  if (value === null) return undefined;
  const v = value.trim().toLowerCase();
  if (v === "false" || v === "0" || v === "no" || v === "off") return false;
  return true;
}

/**
 * Read the connection + shell-consumable attributes off a `<voice-agent>` element.
 * Attributes not yet supported (avatar, dismissible) are intentionally
 * ignored in this slice.
 */
export function parseAttributes(el: Element): MountVoiceAgentOptions {
  const provider = el.getAttribute("provider");
  if (!provider) {
    throw new Error("<voice-agent>: the `provider` attribute is required.");
  }

  const widgetId = el.getAttribute("widget-id") ?? undefined;
  const rawConfig = el.getAttribute("provider-config");
  let config: Record<string, unknown>;
  if (rawConfig !== null) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawConfig);
    } catch (e) {
      throw new Error(
        `<voice-agent>: invalid provider-config JSON — ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    // JSON.parse accepts null / arrays / primitives; provider-config must be a
    // plain object (otherwise Object.keys/parseConfig downstream misbehave).
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      throw new Error("<voice-agent>: provider-config must be a JSON object.");
    }
    config = parsed as Record<string, unknown>;
  } else if (widgetId) {
    config = {};
  } else {
    throw new Error("<voice-agent>: supply a `provider-config` JSON attribute or a `widget-id`.");
  }

  const ui: Ui = {};
  const variant = el.getAttribute("variant");
  if (variant) ui.variant = variant as Ui["variant"];
  const placement = el.getAttribute("placement");
  if (placement) ui.placement = placement as Ui["placement"];
  const startLabel = el.getAttribute("start-label");
  if (startLabel?.trim()) ui.startLabel = startLabel;
  const defaultExpanded = parseBool(el.getAttribute("default-expanded"));
  if (defaultExpanded !== undefined) ui.defaultExpanded = defaultExpanded;
  // Mic mute button is opt-in: absent -> shell default (off).
  const muteButton = parseBool(el.getAttribute("mute-button"));
  if (muteButton !== undefined) ui.muteButton = muteButton;
  const showTranscript = parseBool(el.getAttribute("show-transcript"));
  if (showTranscript !== undefined) ui.showTranscript = showTranscript;
  const textInput = parseBool(el.getAttribute("text-input"));
  if (textInput !== undefined) ui.textInput = textInput;
  const theme = parseThemeAttribute(el.getAttribute("theme"));
  if (theme) ui.theme = theme;
  const accentColor = parseAccentColorAttribute(el.getAttribute("accent-color"));
  if (accentColor) ui.accentColor = accentColor;
  const rawOrbPalette = el.getAttribute("orb-palette");
  if (rawOrbPalette?.trim()) {
    ui.orbPalette = parseOrbPaletteAttribute(rawOrbPalette);
  }
  const rawLangStrings = el.getAttribute("lang-strings");
  if (rawLangStrings?.trim()) {
    ui.langStrings = parseLangStringsAttribute(rawLangStrings);
  }

  const opts: MountVoiceAgentOptions = { provider, config };
  if (widgetId) opts.widgetId = widgetId;
  const authEndpoint = el.getAttribute("auth-endpoint");
  if (authEndpoint) opts.authEndpoint = authEndpoint;
  const fetchCredentials = el.getAttribute("fetch-credentials");
  if (fetchCredentials) opts.fetchCredentials = fetchCredentials as RequestCredentials;
  const telemetryConsole = parseBool(el.getAttribute("telemetry-console"));
  if (telemetryConsole !== undefined) opts.telemetryConsole = telemetryConsole;
  if (Object.keys(ui).length > 0) opts.ui = ui;
  return opts;
}
